import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from '../context/AuthContext';
import axios from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./UserActivity.css";
import MenuItems from '../menubar/MenuItems';
import UserDots from '../menubar/UserDots';
import { getMemoListUrl } from '../constants';
import Logo from '../menubar/Logo';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { FaTimes } from "react-icons/fa";
import { FaDownload } from 'react-icons/fa'; // Import FontAwesome download icon
import MobileMenuBar from '../menubar/MobileMenuBar';
import useMediaQuery from "@mui/material/useMediaQuery";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "transparent",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "none",
}));

const UserActivity = () => {
    const { token, logout } = useContext(AuthContext);
    const [searchQuery, setSearchQuery] = useState("");
    const [activityLogs, setActivityLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const isMobile = useMediaQuery("(max-width:768px)");

    useEffect(() => {
        setLoading(true);
        axios.get(getMemoListUrl(), {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
            .then(response => {
                if (response.data && response.data.data) {
                    setActivityLogs(response.data.data);
                }
            })
            .catch(error => {
                console.error("Error fetching watch time records:", error);
                if (error.response && error.response.status === 403) {
                    logout();
                    navigate('/');
                }
            })
            .finally(() => setLoading(false));
        updateRowsPerPage();
        window.addEventListener("resize", updateRowsPerPage);
        return () => window.removeEventListener("resize", updateRowsPerPage);
    }, []);

    const updateRowsPerPage = () => {
        const screenHeight = window.innerHeight;
        if (screenHeight >= 1440) {   // Large screens
            setRowsPerPage(30);
        } else if (screenHeight >= 960) {  // Medium screens
            setRowsPerPage(20);
        } else {   // Small screens
            setRowsPerPage(10);
        }
    };

    const formatTime = (seconds) => {
        const totalSeconds = Math.ceil(seconds);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return `${h > 0 ? h + "h" : ""}${m > 0 ? m + "m" : ""}${s > 0 ? s + "s" : ""}`;
    };

    const filteredLogs = activityLogs.filter((log) =>
        Object.values(log).some(value => value && value.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("User Activity");

        worksheet.columns = [
            { header: "Empl No", key: "emp_no", width: 15 },
            { header: "Employee Name", key: "emp_name", width: 25 },
            { header: "Module", key: "module", width: 20 },
            { header: "Video Name", key: "p_name", width: 30 },
            { header: "Duration", key: "p_duration", width: 15 },
            { header: "Watch Time", key: "p_watch", width: 15 },
            { header: "Event Time", key: "event_at", width: 25 },
        ];

        worksheet.addRows(filteredLogs.map(log => ({
            ...log,
            p_watch: formatTime(log.p_watch),
            p_duration: formatTime(log.p_duration)
        })));

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "UserActivity.xlsx");
    };

    const formatEventTime = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return format(date, "yyyy-MM-dd HH'h'mm'm'ss's'");
    };

    return (
        <Box className="user-activity-container">
            {loading && <div className="loading-overlay"><span className="spinner"></span></div>}
            <Grid container spacing={2}>
                
            {isMobile ? (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <MobileMenuBar />
            </div>
            ) : (
                <>
                    <Grid item xs={12}>
                        <Logo />
                        <UserDots />
                    </Grid>

                    <Grid item xs={12} sm={3} md={2.3} lg={2.4} className="user-activity-menu">
                        <Item style={{ paddingLeft: '10px' }}>
                            <MenuItems />
                        </Item>
                    </Grid>
                </>
            )}

                <Grid item xs={12} sm={9} md={9.7} lg={9.6}>
                    <Item style={{ width: "90%" }} className="user-role-search">
                        <div className="p-inputgroup">
                            {/* Search Input with Clear Button (No Gap) */}
                            <div className="search-container">
                                <InputText
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-input"
                                />
                                {searchQuery && (
                                    <Button className="clear-btn" style={{ backgroundColor: "#dc3545" }}
                                        onClick={() => setSearchQuery("")}>
                                        <FaTimes size={16} />
                                    </Button>
                                )}
                            </div>

                            {/* Right-aligned Buttons */}
                            {!isMobile && (
                            <div className="right-buttons">


                                <Button className="export-btn download-btn" onClick={exportToExcel}>
                                    <FaDownload className="download-icon" />&nbsp;Download Excel
                                </Button>
                            </div>
                              )}
                        </div>
                    </Item>

                    <Item style={{ width: "100%" }} className="user-activity-table">
                        <DataTable
                            value={filteredLogs}
                            paginator
                            rows={rowsPerPage}
                            rowsPerPageOptions={[10, 20, 30, 50]}
                            scrollable
                            scrollHeight="calc(100vh - 200px)"
                            style={{ width: "100%" }}
                            footer={
                                <div style={{ textAlign: 'right', paddingRight: '6px', fontWeight: 'bold', fontFamily: 'Calibri' }}>
                                    <span style={{ fontFamily: 'Calibri' }}>Total Records: {filteredLogs.length}</span>
                                </div>
                            }
                        >
                            <Column field="emp_no" header="EmplNo" sortable />
                            <Column field="emp_name" header="EmployeeName" sortable />
                            <Column field="module" header="Module" sortable />
                            <Column field="p_name" header="VideoName" sortable style={{ minWidth: '150px' }} />
                            <Column field="p_duration" header="Duration" sortable body={(rowData) => formatTime(rowData.p_duration)} />
                            <Column field="p_watch" header="WatchTime" sortable body={(rowData) => formatTime(rowData.p_watch)} />
                            <Column field="event_at" header="EventTime" sortable body={(rowData) => formatEventTime(rowData.event_at)} />
                        </DataTable>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserActivity;
